import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BadgeIndianRupee,
  CalendarClock,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Gauge,
  Loader2,
  Tag,
  Timer,
} from "lucide-react";
import { api, apiError, fmtCr } from "@/lib/api";

const KIND_ICONS = {
  term: Tag,
  committed: CalendarClock,
  vetting: BadgeIndianRupee,
  readings: Gauge,
  stale: Timer,
};

/** The chase list, read once and shared by whichever panel needs it. */
export const useChase = (plant = "", kinds = "", fyOnly = false, enabled = true) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    // A Pravartanam Partner has nothing on this list to close — do not even ask for it.
    if (!enabled) return;
    api
      .get("/chase", { params: { plant, kind: kinds, fy: fyOnly ? "true" : "" } })
      .then((r) => setData(r.data))
      .catch((e) => setError(apiError(e)));
  }, [plant, kinds, fyOnly, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  return { chase: data, chaseError: error, reloadChase: load };
};

const UnitLine = ({ unit, kind, onOpen }) => {
  const [open, setOpen] = useState(false);
  const found = (unit.gaps || {})[kind];
  if (!found || !found.count) return null;
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div className="border-b border-border last:border-b-0" data-testid={`chase-${kind}-${unit.plant}`}>
      <button
        type="button"
        data-testid={`chase-${kind}-${unit.plant}-toggle`}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left transition-colors duration-200 hover:bg-muted"
      >
        <span className="flex min-w-0 items-center gap-2">
          <Chevron className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate text-sm font-medium">{unit.name}</span>
        </span>
        <span className="flex shrink-0 items-center gap-3">
          {found.expected_cr > 0 && (
            <span className="font-data text-[11px] text-muted-foreground">
              {fmtCr(found.expected_cr)}
            </span>
          )}
          <span className="font-data text-sm font-bold tabular-nums">{found.count}</span>
        </span>
      </button>
      {open && (
        <ul className="bg-muted/40 px-4 py-2">
          {found.items.map((item) => (
            <li key={item.id || item.code} className="border-b border-border/60 last:border-b-0">
              <button
                type="button"
                data-testid={`chase-item-${item.code}`}
                onClick={() => onOpen(item)}
                className="flex w-full items-baseline justify-between gap-3 py-1.5 text-left transition-colors duration-200 hover:text-primary"
              >
                <span className="min-w-0">
                  <span className="font-data text-[11px] text-muted-foreground">{item.code}</span>
                  <span className="ml-2 text-xs">{item.title}</span>
                  {item.signer && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                      · {item.signer} to sign
                    </span>
                  )}
                  {item.days !== undefined && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-[hsl(var(--watch))]">
                      · {item.days} day(s) untouched
                    </span>
                  )}
                </span>
                {item.expected_cr > 0 && (
                  <span className="font-data shrink-0 text-[11px] text-muted-foreground">
                    {fmtCr(item.expected_cr)}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** One hole, unit by unit, with every usecase named. */
export const ChasePanel = ({ chase, kind, title, testId }) => {
  const navigate = useNavigate();
  if (!chase) return null;
  const meta = (chase.kinds || []).find((k) => k.key === kind);
  const total = (chase.totals || {})[kind] || { count: 0, expected_cr: 0 };
  const Icon = KIND_ICONS[kind] || ClipboardList;
  const open = (item) => navigate(`/projects?plant=${item.plant}&q=${item.code}`);

  return (
    <div className="border border-border bg-card" data-testid={testId || `chase-panel-${kind}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border p-4">
        <div className="min-w-0">
          <div className="label-caps flex items-center gap-2 text-primary">
            <Icon className="h-3.5 w-3.5" /> {title || meta?.label || kind}
          </div>
          <p className="mt-1.5 max-w-lg text-[11px] leading-relaxed text-muted-foreground">
            {meta?.note}
          </p>
        </div>
        <div className="text-right">
          <div
            className="font-data text-2xl font-black leading-none"
            data-testid={`chase-count-${kind}`}
          >
            {total.count}
          </div>
          {total.expected_cr > 0 && (
            <div className="font-data mt-1 text-[11px] text-muted-foreground">
              {fmtCr(total.expected_cr)} riding on it
            </div>
          )}
        </div>
      </div>
      {total.count ? (
        <div>
          {(chase.units || []).map((unit) => (
            <UnitLine key={unit.plant} unit={unit} kind={kind} onOpen={open} />
          ))}
        </div>
      ) : (
        <p className="p-6 text-center text-xs text-muted-foreground">
          Nothing open here — every unit has this filled in.
        </p>
      )}
    </div>
  );
};

/** All five holes at a glance, for Home. */
export const ChaseBoard = ({ chase, error }) => {
  const [picked, setPicked] = useState("");
  const navigate = useNavigate();

  // A role with nothing on this list to close is simply not shown one.
  if (error) return null;
  if (!chase)
    return (
      <div className="flex h-24 items-center justify-center border border-border bg-card">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );

  const open = (item) => navigate(`/projects?plant=${item.plant}&q=${item.code}`);

  return (
    <div data-testid="chase-board">
      <div className="grid gap-px bg-border sm:grid-cols-3 lg:grid-cols-5">
        {(chase.kinds || []).map((k) => {
          const total = (chase.totals || {})[k.key] || { count: 0, expected_cr: 0 };
          const Icon = KIND_ICONS[k.key] || ClipboardList;
          const on = picked === k.key;
          return (
            <button
              key={k.key}
              type="button"
              data-testid={`chase-tile-${k.key}`}
              onClick={() => setPicked(on ? "" : k.key)}
              className={`group flex flex-col justify-between gap-3 p-4 text-left transition-colors duration-200 ${
                on ? "bg-muted" : "bg-card hover:bg-muted/60"
              }`}
            >
              <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                <Icon className="h-3.5 w-3.5" /> {k.label}
              </span>
              <span>
                <span
                  className={`font-data block text-3xl font-black leading-none ${
                    total.count ? "" : "text-muted-foreground"
                  }`}
                >
                  {total.count}
                </span>
                <span className="font-data mt-1 block text-[10px] uppercase tracking-widest text-muted-foreground">
                  {total.expected_cr > 0 ? `${fmtCr(total.expected_cr)} riding on it` : "usecase(s)"}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {picked && (
        <div className="mt-px border border-border bg-card" data-testid={`chase-open-${picked}`}>
          <p className="border-b border-border px-4 py-2.5 text-[11px] text-muted-foreground">
            {(chase.kinds || []).find((k) => k.key === picked)?.note}
          </p>
          {(chase.units || []).map((unit) => (
            <UnitLine key={unit.plant} unit={unit} kind={picked} onOpen={open} />
          ))}
          {!((chase.totals || {})[picked]?.count) && (
            <p className="p-6 text-center text-xs text-muted-foreground">
              Nothing open here — every unit has this filled in.
            </p>
          )}
        </div>
      )}
    </div>
  );
};
